import React, { useState, useEffect, useMemo, useImperativeHandle, forwardRef } from "react";
import { Row, Col, FormGroup, Input } from "reactstrap";
import Select from "components/Select";
import { getActiveColorStyle } from "../../utils/colorUtils";
import { getSelectStyles } from "../../utils/selectStyles";
import { getTheme2SearchFilterSingleSelectStyles } from "utils/theme2MultiSelect";

const PortfolioNotifications = forwardRef(({ data, activeColor, isTheme2 = false, portfolioId, updateMutation }, ref) => {
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const [isLightTheme, setIsLightTheme] = useState(false);
  
  const [settings, setSettings] = useState(data?.letter_generation_settings || []);

  useEffect(() => {
    if (data?.letter_generation_settings) {
      setSettings(data.letter_generation_settings);
    }
  }, [data?.letter_generation_settings]);

  useEffect(() => {
    const checkTheme = () => {
      setIsLightTheme(document.body.classList.contains('white-content'));
    };
    checkTheme();

    const observer = new MutationObserver(checkTheme);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, []);

  const selectStyles = useMemo(
    () =>
      isTheme2
        ? getTheme2SearchFilterSingleSelectStyles(activeColorStyle, true)
        : getSelectStyles(activeColorStyle, isLightTheme),
    [activeColorStyle, isLightTheme, isTheme2]
  );
  const inputStyle = isTheme2 ? { fontSize: "14px" } : undefined;

  const yesNoOptions = [
    { value: false, label: "No" },
    { value: true, label: "Yes" },
  ];

  const handleLetterSettingChange = (index, field, value) => {
    const newSettings = [...settings];
    newSettings[index] = { ...newSettings[index], [field]: value };
    setSettings(newSettings);
  };

  // Expose handleSave to parent via ref
  useImperativeHandle(ref, () => ({
    handleSave: async () => {
      try {
        const payload = {
          letter_generation_settings: settings
        };

        await updateMutation.mutateAsync({
          id: portfolioId,
          data: payload
        });

        return { success: true };
      } catch (error) {
        console.error("Error saving notifications:", error);
        return { success: false, error };
      }
    }
  }));

  return (
    <div className="card" style={{ backgroundColor: activeColorStyle.backgroundColor }}>
      <div className="card-header font-weight-bold text-uppercase">AUTOMATIC LETTER GENERATION SETTINGS</div>
      <div className="card-body">
        <Row>
          <Col md="3" className="font-weight-bold mb-2">Do You Want to Send a:</Col>
          <Col md="2" className="font-weight-bold mb-2"></Col>
          <Col md="4" className="font-weight-bold mb-2">Notice Type</Col>
          <Col md="3" className="font-weight-bold mb-2"># of Days b/f sending</Col>
        </Row>
        {settings.map((setting, index) => {
          // Show days input only for "Auto Reminders/Loan Bills" and "Insurance Reminder Notices"
          const showDaysInput = setting.name === "Auto Reminders/Loan Bills" || setting.name === "Insurance Reminder Notices";
          return (
            <Row key={index} className="mb-2">
              <Col md="3">
                <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                  <span>{setting.name}</span>
                </div>
              </Col>
              <Col md="2">
                <Select
                  activeColor={activeColor}
                  styles={selectStyles}
                  className="react-select"
                  placeholder="Select"
                  name={`send_${index}`}
                  isSearchable={false}
                  closeMenuOnSelect={true}
                  value={yesNoOptions.find(opt => opt.value === setting.send)}
                  onChange={(value) => handleLetterSettingChange(index, 'send', value?.value ?? false)}
                  options={yesNoOptions}
                />
              </Col>
              <Col md="4">
                <Select
                  activeColor={activeColor}
                  styles={selectStyles}
                  className="react-select"
                  placeholder="Select"
                  name={`notice_type_${index}`}
                  isSearchable
                  closeMenuOnSelect={true}
                  value={setting.notice_type ? { value: setting.notice_type, label: setting.notice_type } : null}
                  onChange={(value) => handleLetterSettingChange(index, 'notice_type', value?.value || "")}
                  options={[]}
                  isClearable
                />
              </Col>
              <Col md="3">
                {showDaysInput && (
                  <Input
                    type="number"
                    className="form-control"
                    style={inputStyle}
                    value={setting.days_before || 0}
                    onChange={(e) => handleLetterSettingChange(index, 'days_before', parseInt(e.target.value) || 0)}
                    min="0"
                  />
                )}
              </Col>
            </Row>
          );
        })}
      </div>
    </div>
  );
});

PortfolioNotifications.displayName = "PortfolioNotifications";

export default PortfolioNotifications;

